/**
 * CMP同意状態のCookie読み書き
 *
 * 単一のJSON Cookie（デフォルト: ck_{tenantId}_cmp）に同意状態を保存する。
 * Cookie保存に失敗した場合はメモリ上に保持し、現在のセッション内で動作させる。
 */

// Cookie保存に失敗した場合のメモリフォールバック
let memoryFallback = null;

/**
 * UUID v4を生成する
 * crypto.randomUUID()を優先し、非対応ブラウザではcrypto.getRandomValues()にフォールバック
 * @returns {string}
 */
export function generateUUID() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // crypto.getRandomValues() ベースのフォールバック
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-');
}

/**
 * 同意状態をCookieから読み取る
 * @param {string} cookieName
 * @returns {Object|null} ConsentState or null
 */
export function readConsent(cookieName) {
  // メモリフォールバックがあればそちらを返す
  if (memoryFallback) {
    return memoryFallback;
  }

  try {
    const match = document.cookie.split('; ').find((row) => row.startsWith(cookieName + '='));
    if (!match) return null;

    const value = decodeURIComponent(match.split('=').slice(1).join('='));
    const parsed = JSON.parse(value);

    if (!parsed || !parsed.id || !parsed.categories) return null;
    // categories がプレーンオブジェクトであることを検証（配列や文字列等を拒否）
    if (typeof parsed.categories !== 'object' || Array.isArray(parsed.categories)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 同意状態をCookieに書き込む
 * @param {string} cookieName
 * @param {Object} consent - ConsentState
 * @param {Object} cookieOptions - { maxAge, path, sameSite }
 */
export function writeConsent(cookieName, consent, cookieOptions) {
  const json = JSON.stringify(consent);
  const maxAgeSeconds = cookieOptions.maxAge * 86400;
  const parts = [
    `${cookieName}=${encodeURIComponent(json)}`,
    `max-age=${maxAgeSeconds}`,
    `path=${cookieOptions.path}`,
    `SameSite=${cookieOptions.sameSite}`
  ];
  // HTTPS接続時はSecure属性を付与
  if (typeof location !== 'undefined' && location.protocol === 'https:') {
    parts.push('Secure');
  }
  const cookieStr = parts.join('; ');

  try {
    document.cookie = cookieStr;
    // 書き込み成功を検証（document.cookieは例外をスローしないため）
    if (document.cookie.includes(cookieName + '=')) {
      memoryFallback = null;
    } else {
      memoryFallback = consent;
    }
  } catch {
    // Cookie保存失敗 — メモリ上で保持
    memoryFallback = consent;
  }
}

/**
 * Cookieを削除する
 * @param {string} cookieName
 * @param {string} path
 */
export function deleteConsent(cookieName, path) {
  document.cookie = `${cookieName}=; max-age=0; path=${path}`;
  memoryFallback = null;
}

/**
 * メモリフォールバックを明示的にリセットする（テスト・再初期化用）
 */
export function resetMemoryFallback() {
  memoryFallback = null;
}
